import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { BrandsContext } from "contexts/Brands";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";

import StyledDialog from "components/Styled/Dialog";
import { fetcherAuth } from "utils/api";
import {
  DISPLAYED_INSTAGRAM_ONBOARDING_PAYWALL,
  DISPLAY_SCHEDULED_DIALOG,
  INSTAGRAM_ONBOARDING_CHOICE,
  SCHEDULED_DATE_EMAIL,
} from "utils/localStorage";

const DELETION_REASONS = [
  "Privacy concerns",
  "I'm not using Bento",
  "The app has a poor user experience",
  "The content did not meet my expectations",
  "I'd rather not say",
];

type DialogStep = "reasons" | "confirm" | null;

export default function DeleteAccount() {
  const { getToken, signOut } = useAuth();
  const { setAlert } = useContext(AlertContext);
  const { currentOrg, clearOrganizationContext } = useContext(
    OrganizationUserContext,
  );
  const { clearBrandContext } = useContext(BrandsContext);
  const navigate = useNavigate();

  const [dialogStep, setDialogStep] = useState<DialogStep>(null);
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const handleReasonToggle = (reason: string) => {
    setSelectedReasons((prev) =>
      prev.includes(reason)
        ? prev.filter((r) => r !== reason)
        : [...prev, reason],
    );
  };

  const handleClose = () => {
    setDialogStep(null);
    setSelectedReasons([]);
  };

  const handleShareReason = async () => {
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/settings/share-deletion-reason`,
        "POST",
        {},
        { reason: selectedReasons },
      );
    } catch {
      // Non-critical — proceed to confirmation even if this fails
    }
    setDialogStep("confirm");
  };

  const handleConfirmDelete = async () => {
    setLoading(true);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/settings`,
        "DELETE",
        {},
        {},
      );
      await signOut(() => navigate("/sign-in", { replace: true }));
      clearBrandContext();
      clearOrganizationContext();
      localStorage.removeItem(`${DISPLAY_SCHEDULED_DIALOG}-${currentOrg?.id}`);
      localStorage.removeItem(`${SCHEDULED_DATE_EMAIL}-${currentOrg?.id}`);
      localStorage.removeItem(
        `${DISPLAYED_INSTAGRAM_ONBOARDING_PAYWALL}-${currentOrg?.id}`,
      );
      localStorage.removeItem(INSTAGRAM_ONBOARDING_CHOICE);
    } catch (error) {
      setAlert(
        error.message || "An error occurred when deleting your account.",
        "warning",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box>
        <Typography variant="h6" gutterBottom>
          Delete Account
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Looking to delete your account and all associated data?
        </Typography>
        <Button
          variant="outlined"
          color="error"
          onClick={() => setDialogStep("reasons")}
        >
          Continue
        </Button>
      </Box>

      {/* Step 1: Gather deletion reasons */}
      <StyledDialog
        title="Delete Account"
        open={dialogStep === "reasons"}
        handleClose={handleClose}
        maxWidth="sm"
        actionContainer={
          <>
            <Button onClick={handleClose}>Close</Button>
            <Button
              variant="contained"
              onClick={handleShareReason}
              disabled={selectedReasons.length === 0}
            >
              Continue
            </Button>
          </>
        }
      >
        <Typography sx={{ mb: 2 }}>
          Before you go, we'd love to hear why to gather feedback and continue
          to improve our platform. Please select all reasons that apply below:
        </Typography>
        <FormGroup>
          {DELETION_REASONS.map((reason) => (
            <FormControlLabel
              key={reason}
              control={
                <Checkbox
                  checked={selectedReasons.includes(reason)}
                  onChange={() => handleReasonToggle(reason)}
                />
              }
              label={reason}
            />
          ))}
        </FormGroup>
      </StyledDialog>

      {/* Step 2: Confirm deletion */}
      <StyledDialog
        title="Are you sure?"
        open={dialogStep === "confirm"}
        handleClose={handleClose}
        maxWidth="sm"
        actionContainer={
          <>
            <Button onClick={handleClose}>Close</Button>
            <LoadingButton
              loading={loading}
              variant="contained"
              color="error"
              onClick={handleConfirmDelete}
            >
              Confirm
            </LoadingButton>
          </>
        }
      >
        <Typography>
          Please click confirm below to go forward with the deletion of your
          account.{" "}
          <strong>
            This process cannot be undone and all your data will be permanently
            deleted.
          </strong>
        </Typography>
      </StyledDialog>
    </>
  );
}
